

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// styles
import {
  IconBtnContainer,
  IconBtnMenu,
  IconBtnMenuContent,
  IconBtnMenuItem
} from './IconBtn.styles.js';

export default function IconBtn({
  children,
  onClick,
  label,
  title,
  disabled = false,
  type = 'button',
  className,
  options = [],
  variant = 'icon',
  size = 'md',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const hasMenu = options.length > 0;

  const handleButtonClick = (event) => {
    if (disabled) return;
    if (!hasMenu) {
      onClick?.(event);
      return;
    }
    event.stopPropagation();
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = Math.min(200, options.length * 44 + 16);
    const margin = 10;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const showAbove = spaceBelow < menuHeight;
    const top = showAbove
      ? Math.max(margin, rect.top - menuHeight - 6)
      : Math.min(rect.bottom + 6, window.innerHeight - menuHeight - margin);
    const right = Math.max(margin, window.innerWidth - rect.right);

    setMenuPosition({ top, right });
  }, [isOpen, options.length]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (buttonRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const menu = hasMenu && isOpen ? createPortal(
    <IconBtnMenu
      ref={menuRef}
      style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
      className='panel-bg'
    >
      <IconBtnMenuContent>
        <div />
        <ul>
          {options.map((option, index) => (
            <IconBtnMenuItem
              key={`${option.label}-${index}`}
              onClick={(event) => {
                event.stopPropagation();
                option.onClick?.(event);
                setIsOpen(false);
              }}
            >
              {option.label}
            </IconBtnMenuItem>
          ))}
        </ul>
      </IconBtnMenuContent>
    </IconBtnMenu>,
    document.body
  ) : null;

  return (
    <>
      <IconBtnContainer
        ref={buttonRef}
        type={type}
        onClick={handleButtonClick}
        aria-label={label}
        title={title || label}
        disabled={disabled}
        className={className}
        $variant={variant}
        $size={size}
      >
        {children}
      </IconBtnContainer>
      {menu}
    </>
  );
}